import { FilmIcon, MusicNoteIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

const videoFormats = ['MP4', 'MKV', 'FLV', 'AVI', 'MOV', 'WMV'];
const audioFormats = ['M4A', 'MP3', 'OGG', 'AAC', 'WAV', 'FLAC', 'WMA'];

// Mapping of formats to their converter page URLs
const formatLinks: Record<string, string> = {
  MP3: '/ai-transcription/mp3-to-text-converter',
  MP4: '/ai-transcription/mp4-to-text-converter',
  M4A: '/ai-transcription/m4a-to-text-converter',
  WAV: '/ai-transcription/wav-to-text-converter',
};

export default function SupportedFormats() {
  const { t } = useLocale('ai-transcription');

  const renderFormat = (format: string, colorClasses: string) => {
    const hasLink = formatLinks[format];

    const content = (
      <span
        className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${colorClasses}`}
      >
        {format}
      </span>
    );

    if (hasLink) {
      return (
        <Link
          key={format}
          href={hasLink}
          className='transition-transform hover:scale-105'
        >
          {content}
        </Link>
      );
    }

    return <span key={format}>{content}</span>;
  };

  return (
    <div className='relative isolate overflow-hidden bg-white py-24 dark:bg-slate-900/50'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <div className='mb-4 flex justify-center'>
            <div className='relative inline-flex items-center space-x-2 rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-500/20 flex-shrink-0'>
              <svg
                className='w-4 h-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'
                />
              </svg>
              <span>{t('Universal Compatibility')}</span>
            </div>
          </div>
          <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 dark:text-slate-200 sm:text-5xl lg:text-balance'>
            {t('Supported Formats')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-400 lg:text-balance'>
            {t(
              'We support a wide range of file formats for converting audio and video files to text. Drop any of these files into our dashboard and get started instantly.',
            )}
          </p>
        </div>

        <div className='mx-auto mt-16 max-w-4xl'>
          <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
            {/* Video Formats */}
            <div className='group relative flex flex-col rounded-3xl bg-blue-50/50 p-8 shadow-sm ring-1 ring-gray-900/5 backdrop-blur-md dark:bg-slate-800/30 dark:ring-white/10 hover:-translate-y-1 hover:shadow-xl hover:ring-blue-500/30 transition-all duration-300'>
              <div className='absolute inset-0 bg-gradient-to-br from-blue-100/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-blue-900/10 rounded-3xl' />

              <div className='relative flex items-center gap-4'>
                <div className='flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/10 group-hover:scale-110 transition-transform duration-300'>
                  <FilmIcon
                    className='h-8 w-8 text-blue-500'
                    aria-hidden='true'
                  />
                </div>
                <h3 className='text-2xl font-bold text-gray-900 dark:text-slate-100 drop-shadow-sm'>
                  {t('Video')}
                </h3>
              </div>
              <div className='relative mt-8 flex flex-wrap gap-3'>
                {videoFormats.map((format) =>
                  renderFormat(
                    format,
                    'bg-white text-blue-700 shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:text-blue-300 dark:ring-white/10 hover:shadow-md hover:ring-blue-500/50 hover:bg-blue-50 dark:hover:bg-slate-700 font-bold px-4 py-2 rounded-xl text-md cursor-pointer transition-all',
                  ),
                )}
              </div>
            </div>

            {/* Audio Formats */}
            <div className='group relative flex flex-col rounded-3xl bg-purple-50/50 p-8 shadow-sm ring-1 ring-gray-900/5 backdrop-blur-md dark:bg-slate-800/30 dark:ring-white/10 hover:-translate-y-1 hover:shadow-xl hover:ring-purple-500/30 transition-all duration-300'>
              <div className='absolute inset-0 bg-gradient-to-br from-purple-100/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-purple-900/10 rounded-3xl' />

              <div className='relative flex items-center gap-4'>
                <div className='flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/10 group-hover:scale-110 transition-transform duration-300'>
                  <MusicNoteIcon
                    className='h-8 w-8 text-purple-500'
                    aria-hidden='true'
                  />
                </div>
                <h3 className='text-2xl font-bold text-gray-900 dark:text-slate-100 drop-shadow-sm'>
                  {t('Audio')}
                </h3>
              </div>
              <div className='relative mt-8 flex flex-wrap gap-3'>
                {audioFormats.map((format) =>
                  renderFormat(
                    format,
                    'bg-white text-purple-700 shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:text-purple-300 dark:ring-white/10 hover:shadow-md hover:ring-purple-500/50 hover:bg-purple-50 dark:hover:bg-slate-700 font-bold px-4 py-2 rounded-xl text-md cursor-pointer transition-all',
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
